import {Component, OnInit, ViewChild} from '@angular/core';
import {FormControl, FormGroup} from "@angular/forms";
import {ActivatedRoute} from "@angular/router";
import {ComponentType} from '@angular/cdk/overlay';

import {map, Observable, startWith} from "rxjs";

import {Customer, GrantComponent} from '../../../core/models';
import {CustomersService, GrantComponentsService} from "../../../core/services";
import {listAnimation} from "../../../core/animations/list-animation";
import {
  ApprovalConfirmationSettingsDialogComponent,
  ArchiveSettingsDialogComponent,
  AutomationSettingsDialogComponent,
  CriteriaSettingsDialogComponent,
  ForecastSettingsDialogComponent,
  PaymentReceiptsSettingsDialogComponent,
  PortalSettingsDialogComponent,
  RecommendationsSettingsDialogComponent,
  RefusalSettingsDialogComponent,
  ScoringSettingsDialogComponent,
  SplitGrantsSettingsDialogComponent,
  TaxSettingsDialogComponent
} from './component-settings';
import {
  BulkActionSettingsDialogComponent
} from "./component-settings/bulk-action-settings-dialog/bulk-action-settings-dialog.component";
import {
  EmployeeNumberSettingsDialogComponent
} from "./component-settings/employee-number-settings-dialog/employee-number-settings-dialog.component";
import {AISettingsDialogComponent} from './component-settings/ai-settings-dialog/ai-settings-dialog.component';
import {MatSelectionList} from "@angular/material/list";
import {MatDialog} from "@angular/material/dialog";
import {compact} from '../../../core/helpers/compact';
import {
  ForeignCurrencySettingsDialogComponent
} from "./component-settings/foreign-currency-settings-dialog/foreign-currency-settings-dialog.component";

@Component({
    selector: 'app-customer-components',
    templateUrl: './customer-components.component.html',
    styleUrls: ['./customer-components.component.scss'],
    animations: [listAnimation],
    standalone: false
})
export class CustomerComponentsComponent implements OnInit {
  @ViewChild('componentsSelection') componentsSelection: MatSelectionList;
  customer: Customer;

  settingsFormGroup: FormGroup;

  allComponents: GrantComponent[] = [];

  selectedComponents: number[] = [];
  searchString = new FormControl('', {nonNullable: true});
  filteredComponents$: Observable<{name: string, id: number, slug: string }[]>;

  showSelected: boolean = true;

  constructor(private customersService: CustomersService,
              private componentsService: GrantComponentsService,
              private dialog: MatDialog,
              private route: ActivatedRoute) {
  }

  ngOnInit(): void {
    this.settingsFormGroup = new FormGroup({});
    this.route.data.subscribe((data) => {
      this.customer = data['customer'];
    });
    this.componentsService.getComponents()
      .subscribe(components => {
        this.allComponents = components.filter(c => !c.settings.hidden);
        this.componentsInit();
      });
  }

  selectComponent(componentId: number, checked: boolean) {
    if (checked) {
      this.selectedComponents.push(componentId);
    } else {
      const index = this.selectedComponents.indexOf(componentId);
      this.selectedComponents.splice(index, 1);
    }
    this.saveComponents();
  }

  editComponentSettings(componentSlug: string): void {
    const settingsComponent = this.getSettingsDialog(componentSlug);
    if(null === settingsComponent) {
      return;
    }
    // open dialog
    const panelClasses = ['dialog'];
    if (componentSlug === 'recommendations') {
      panelClasses.push('no-unset-height');
    }
    this.dialog.open(settingsComponent, {
      data: {
        ...this.customer.settings,
        customer: this.customer
      },
      panelClass: panelClasses,
      minWidth: '800px'
    }).afterClosed()
      .subscribe((result) => {
        if (result) {
          if (this.settingsFormGroup.get(componentSlug)) {
            this.settingsFormGroup.removeControl(componentSlug);
          }
          switch(componentSlug) {
            case 'automatic_approval_confirmation':
              this.settingsFormGroup.addControl('approval_confirmation', result);
              break;
            case 'portal':
              this.settingsFormGroup.removeControl('notifications');
              this.settingsFormGroup.addControl('notifications', result);
              break;
            case 'recommendations':
              const styleConfig = compact(result.get('styleConfig')?.value);
              result.removeControl('styleConfig');
              if (!!styleConfig && result.get('style').value !== 'ap') {
                result.removeControl('style');
                result.addControl('style', new FormControl(styleConfig));
              }
              this.settingsFormGroup.addControl(componentSlug, result);
              break;
            default:
              this.settingsFormGroup.addControl(componentSlug, result);
          }
          this.saveSettings();
        }
      });
  }

  componentsInit(): void {
    this.selectedComponents = this.customer.components.map(c => c.id).sort();
    this.allComponents.sort((a, b) => a.name.localeCompare(b.name));
    this.filteredComponents$ = this.searchString.valueChanges.pipe(startWith(''))
      .pipe(map(search => this.allComponents.filter(c => c.name.toLocaleLowerCase().includes(search.toLowerCase()))));
  }

  saveComponents(): void {
    this.componentsService.saveComponents(this.selectedComponents, this.customer.id)
      .subscribe(value => {
        this.customer = value;
      });
  }

  componentHasSettings(componentSlug: string): boolean {
    return null !== this.getSettingsDialog(componentSlug);
  }

  isSelected(componentSlug: string): boolean {
    return this.customer.components.filter(component => component.slug === componentSlug).length > 0;
  }

  saveSettings(): void {
    this.customer.settings = Object.assign({}, this.customer.settings, this.settingsFormGroup.value);
    console.log(this.settingsFormGroup.value)
    this.customersService.update(this.customer)
      .subscribe(value => {
        this.customer = value;
      })
  }

  getSettingsDialog(slug: string): ComponentType<any> | null {
    switch(slug) {
      case 'automatic_approval_confirmation':
        return ApprovalConfirmationSettingsDialogComponent;
      case 'archive':
        return ArchiveSettingsDialogComponent;
      case 'automation':
        return AutomationSettingsDialogComponent;
      case 'criteria':
        return CriteriaSettingsDialogComponent;
      case 'forecast':
        return ForecastSettingsDialogComponent;
      case 'payment-receipts':
        return PaymentReceiptsSettingsDialogComponent;
      case 'recommendations':
        return RecommendationsSettingsDialogComponent;
      case 'paper-refusal':
        return RefusalSettingsDialogComponent;
      case 'scoring':
        return ScoringSettingsDialogComponent;
      case 'bulk_actions':
        return BulkActionSettingsDialogComponent;
      case 'portal' :
        return PortalSettingsDialogComponent;
      case 'employee_no':
        return EmployeeNumberSettingsDialogComponent;
      case 'split-grants':
        return SplitGrantsSettingsDialogComponent;
      case 'tax-report':
        return TaxSettingsDialogComponent;
      case 'ai':
        return AISettingsDialogComponent;
      case 'foreign-currency':
        return ForeignCurrencySettingsDialogComponent;
      default:
        return null;
    }
  }
}
